/**
 * The new-order and edit forms for custom orders: the sheet's own choices, the partner's supplier
 * suggestion rule, and an existing order turned into values to edit or duplicate.
 */
import { localized, t } from "./i18n"
import type { CustomIntakeItem, CustomOrderDetail, NewCustomOrderBody, SheetField, SheetMismatch } from "./medusa"

export const SHEET_FIELD_LABEL: Record<SheetField, string> = localized(
  {
    handle: "Instagram",
    en: "items",
    yuan: "¥ cost",
    link: "links",
    sale: "sale price",
    shipping: "delivery",
    prepay: "deposit",
    prepay_status: "deposit status",
    larry: "Larry status",
    number: "order number",
    prepay_method: "deposit method",
    customer_info: "customer details",
  },
  "sheet field"
)

/** "The sheet shows X for Y" — one line per cell the read-back found different. */
export function mismatchLines(mismatches: SheetMismatch[] | undefined): string[] {
  return (mismatches ?? []).map((m) =>
    t("{field}: sent «{expected}», the sheet shows «{shown}»", { field: SHEET_FIELD_LABEL[m.field] ?? m.field, expected: m.expected || t("(empty)"), shown: m.shown || t("(empty)") })
  )
}

/** An item line in a form: numbers kept as the text typed. */
/** `zh`: the options as the seller wrote them on the Taobao / Weidian page, for Larry (16 Oct 2026). */
export type ItemDraft = { en: string; zh?: string; link: string; yuan: string; eur: string; photo: string | null; pick_id?: string }
export const emptyItem = (): ItemDraft => ({ en: "", link: "", yuan: "", eur: "", photo: null })

/** "69", "69,5", "¥ 1.200" → a number; "" → null; nonsense → NaN. */
export function toNumber(text: string): number | null {
  const t = text.trim().replace(/[€¥\s]/g, "")
  if (!t) return null
  const value = Number(t.replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", "."))
  return Number.isFinite(value) ? value : Number.NaN
}

export function itemsOut(items: ItemDraft[], photoFor: (url: string) => string): CustomIntakeItem[] {
  return items
    .filter((item) => item.en.trim() || item.link.trim() || item.yuan.trim() || item.eur.trim())
    .map((item) => ({ en: item.en.trim(), ...(item.zh?.trim() ? { zh: item.zh.trim() } : {}), ...(item.pick_id ? { pick_id: item.pick_id } : {}), link: item.link.trim(), yuan: toNumber(item.yuan), eur: toNumber(item.eur), images: item.photo ? [photoFor(item.photo)] : [] }))
}

/** The sheet's free-text delivery cell → the dropdown value it means ("Άλλο" with the rest as its note). */
export function shippingChoice(raw: string, choices: string[]): { shipping: string; note: string } {
  const t = raw.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()
  const pick = (needle: RegExp) => choices.find((c) => needle.test(c.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()))
  const found = /box/.test(t) ? pick(/box/) : /meet|συναντ|χερι/.test(t) ? pick(/meet/) : /οικον/.test(t) ? pick(/οικον/) : /ελτα|elta/.test(t) ? pick(/ελτα/) : undefined
  if (found) return { shipping: found, note: "" }
  return { shipping: "Άλλο", note: raw.replace(/^Άλλο:\s*/i, "").trim() }
}

export function methodChoice(raw: string, methods: string[]): string {
  const t = raw.trim().toLowerCase()
  if (!t) return methods[0]
  return methods.find((m) => m.toLowerCase() === t) ?? methods.find((m) => t.includes(m.toLowerCase())) ?? "Άλλο"
}

/** "10€", "όλο το ποσό", "" → the amount to put in the deposit field. */
export function depositAmountText(raw: string): string {
  const m = raw.replace(",", ".").match(/\d+(\.\d+)?/)
  return m ? m[0] : ""
}

/** What the forms edit, from a mirrored order or from an order HQ created on the test copy. */
export type EditableOrder = {
  order_number: string
  handle: string
  shipping_raw: string
  prepay_amount: string
  prepay_method: string
  prepay_status: string
  customer_info: string
  items: ItemDraft[]
  sent_to_larry: boolean
}

/** An item photo as the sheet has it (w960) back to the w1600 address the form keeps. */
export const formPhoto = (url: string | null | undefined): string | null => (url ? url.replace(/-w960\.(jpg|webp)$/, "-w1600.$1") : null)

export function editableFromMirror(order: CustomOrderDetail, photos: string[] = [], zh: string[] = []): EditableOrder {
  const single = order.items.length === 1
  return {
    order_number: order.order_number,
    handle: order.handle.replace(/^@/, "").trim(),
    shipping_raw: order.shipping_raw,
    prepay_amount: order.prepay_amount,
    prepay_method: order.prepay_method,
    prepay_status: order.prepay_status,
    customer_info: order.customer_info,
    items: order.items.map((item, index) => ({
      en: item.en,
      zh: zh[index] ?? "",
      link: /^https?:/i.test(item.link) ? item.link : "",
      yuan: item.yuan != null ? String(item.yuan) : "",
      eur: item.sale_eur != null ? String(item.sale_eur) : single && order.sale_total != null ? String(order.sale_total) : "",
      photo: formPhoto(photos[index]),
    })),
    sent_to_larry: !!order.larry_status.trim() && order.larry_status.trim().toUpperCase() !== "NOT ORDERED",
  }
}

export function editableFromIntake(order_number: string, order: NewCustomOrderBody & { sent_to_larry?: boolean }): EditableOrder {
  return {
    order_number,
    handle: order.handle,
    shipping_raw: order.shipping === "Άλλο" && order.shipping_note ? `Άλλο: ${order.shipping_note}` : order.shipping,
    prepay_amount: order.prepay_amount == null ? "" : String(order.prepay_amount),
    prepay_method: order.prepay_method,
    prepay_status: order.prepay_status,
    customer_info: order.customer_info ?? "",
    items: order.items.map((item) => ({ en: item.en, zh: item.zh ?? "", link: item.link, yuan: item.yuan != null ? String(item.yuan) : "", eur: item.eur != null ? String(item.eur) : "", photo: formPhoto(item.images?.[0]) })),
    sent_to_larry: !!order.sent_to_larry,
  }
}

// ── The partner's supplier suggestions (colour words kept) ─────────────────────────────────────

const NOISE = new Set(["full", "packaging", "packing", "apo", "stock", "stok", "στοκ", "με", "και", "the", "a", "size", "color", "colour", "otan", "ertho", "erthoun", "erthei", "come", "when"])
const tokens = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "").replace(/\(.*?\)/g, " ").replace(/[^a-z0-9α-ω ]/gi, " ").split(/\s+/).filter(Boolean)
function keyTokens(s: string) {
  const all = tokens(s)
  const key = all.filter((t) => !NOISE.has(t) && t.length > 1)
  return key.length ? key : all
}

export function matchSuppliers<T extends { title: string }>(description: string, suppliers: T[], limit = 3): T[] {
  const words = new Set(tokens(description))
  if (!words.size) return []
  return suppliers
    .map((supplier) => {
      const key = keyTokens(supplier.title)
      const hit = key.filter((t) => words.has(t)).length
      return { supplier, hit, ratio: key.length ? hit / key.length : 0, size: key.length }
    })
    .filter((m) => m.size > 0 && m.hit >= Math.min(2, m.size) && m.ratio >= 0.6)
    .sort((a, b) => b.hit + b.ratio - (a.hit + a.ratio))
    .slice(0, limit)
    .map((m) => m.supplier)
}
